import { Field, FormBlock, Select } from '@westhetics/ui'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { useNetworkOptions } from '@/features/networks/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'

/**
 * A rede da clínica (RN-ADM-54, RT-06; P-ADM-83): um `Select` entre as redes existentes, com "Sem
 * rede". A clínica que já pertence a uma rede só pode sair dela por aqui: o select oferece a atual e
 * "Sem rede", e a troca é feita em dois salvamentos ou pela tela da rede. É a regra de não mover em
 * silêncio, na tela.
 */
export function NetworkBlock({
  value,
  onChange,
  current,
  error,
}: {
  /** O id da rede escolhida; vazio é "Sem rede". */
  value: string
  onChange: (networkId: string) => void
  /** A rede salva da clínica, no detalhe; na criação, não há. */
  current?: { id: string; name: string } | null
  error?: string
}) {
  const locked = Boolean(current)
  // Com rede, o select só oferece a atual e "Sem rede": a lista das outras não é pedida.
  const options = useNetworkOptions({ enabled: !locked })

  let hint: ReactNode = 'Sem rede, a clínica lê só os próprios pacientes.'
  if (options.isError) {
    hint = (
      <>
        {errorMessage(options.error)}{' '}
        <button
          type="button"
          onClick={() => void options.refetch()}
          className="hover:text-ink cursor-pointer underline underline-offset-4"
        >
          Tentar de novo
        </button>
      </>
    )
  } else if (current) {
    hint = (
      <>
        Para mudar de rede, escolha Sem rede e salve; depois escolha a nova.{' '}
        <Link
          to={routes.admin.network(current.id)}
          className="hover:text-ink underline underline-offset-4"
        >
          abrir a rede
        </Link>
      </>
    )
  } else if (options.data && options.data.length === 0) {
    hint = (
      <>
        Nenhuma rede cadastrada ainda.{' '}
        <Link to={routes.admin.newNetwork} className="hover:text-ink underline underline-offset-4">
          Criar uma rede
        </Link>
      </>
    )
  }

  const choices = locked
    ? [{ id: current!.id, name: current!.name, sharesPatients: null }]
    : (options.data ?? []).map((network) => ({
        id: network.id,
        name: network.name,
        sharesPatients: network.sharesPatients,
      }))

  return (
    <FormBlock
      title={
        <>
          Rede
          <span className="text-label-soft tracking-normal normal-case"> · opcional</span>
        </>
      }
      note="Clínicas da mesma rede podem ler pacientes umas das outras."
    >
      <Field label="Rede" optional error={error} hint={hint} className="tablet:max-w-[420px]">
        <Select
          value={value}
          placeholder={options.isPending && !locked ? 'Carregando as redes…' : 'Sem rede'}
          disabled={options.isPending && !locked}
          onChange={(event) => onChange(event.target.value)}
        >
          {choices.map((network) => (
            <option key={network.id} value={network.id}>
              {network.name}
              {network.sharesPatients ? ' · lê pacientes' : ''}
            </option>
          ))}
        </Select>
      </Field>
    </FormBlock>
  )
}
